import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import { T } from "../constants/theme";
import { CartContext } from "../contexts/CartContext";
import { AuthContext } from "../contexts/AuthContext";
import { ToastContext } from "../contexts/ToastContext";
import { WishlistContext } from "../contexts/WishlistContext";
import { requireLogin } from "../utils/authHelpers";

export default function ProductCard({ product, onView }) {
  const navigate = useNavigate();
  const { addItem } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const { isWishlisted, toggleWishlist } = useContext(WishlistContext);

const [hovered, setHovered] = useState(false);
const wishlisted = isWishlisted(product.id);

  const hasDiscount =
    product.sale_price &&
    product.sale_price < product.price;

  const discount = hasDiscount
    ? Math.round(
        (1 - product.sale_price / product.price) * 100
      )
    : 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        overflow: "hidden",
        background: T.surface,
        borderRadius: 20,
        border: `1px solid ${T.border}`,
        boxShadow: hovered ? T.shadowMedium : T.shadowSoft,
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "transform .3s ease, box-shadow .3s ease",
      }}
    >
      {/* IMAGE */}
      <div
        onClick={() => onView(product.slug)}
        style={{
          position: "relative",
          overflow: "hidden",
          background: T.paper,
          aspectRatio: "3 / 4",
        }}
      >
        <img
          src={product.image}
          alt={product.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform .7s ease",
            transform: hovered ? "scale(1.08)" : "scale(1)",
          }}
        />

        {/* Dark Overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,.08)",
            opacity: hovered ? 1 : 0,
            transition: ".3s",
          }}
        />

        {/* BADGES */}
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {product.is_featured === 1 && (
            <div
              style={{
                background: T.surface,
                padding: "5px 10px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                color: T.ink,
              }}
            >
              NEW
            </div>
          )}
          
        </div>

        {/* HOVER BUTTONS */}
        <div
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            right: 20,
            display: "flex",
            gap: 10,
            opacity: hovered ? 1 : 0,
            transform: hovered
              ? "translateY(0)"
              : "translateY(25px)",
            transition: ".4s",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!requireLogin(user, navigate, toast, "Please sign in to add items to your shopping bag.")) {
                return;
              }
              addItem(product.id);
            }}
            style={{
              flex: 1,
              height: 46,
              border: "none",
              background: T.surface,
              color: T.ink,
              cursor: "pointer",
              fontWeight: 700,
              letterSpacing: ".15em",
              textTransform: "uppercase",
              fontSize: 11,
            }}
          >
            Invest
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(product.slug);
            }}
            style={{
              width: 46,
              border: "none",
              background: T.surface,
              color: T.ink,
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            ↗
          </button>
        </div>
      </div>
      {/* Wishlist */}
<button
  onClick={(e) => {
    e.stopPropagation();
    if (!requireLogin(user, navigate, toast, "Please sign in to save items to your wishlist.")) {
      return;
    }
    toggleWishlist(product);
  }}
  style={{
    position: "absolute",
    top: 16,
    right: 16,
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "none",
    outline: "none",
    background: wishlisted ? T.gold : T.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 999,
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    transition: "all .3s ease",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = "scale(1.08)";
    if (!wishlisted) {
      e.currentTarget.style.background = T.goldLight;
    }
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = "scale(1)";
    if (!wishlisted) {
      e.currentTarget.style.background = T.surface;
    }
  }}
>
  {wishlisted ? (
    <FaHeart size={17} color={T.paper} />
  ) : (
    <FiHeart size={17} color={T.ink} />
  )}
</button>

      {/* PRODUCT INFO */}
      <div
        style={{
          padding: "18px 10px 8px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: T.muted,
            textTransform: "uppercase",
            letterSpacing: ".2em",
            marginBottom: 8,
          }}
        >
          {product.brand_name || "CropBank"}
        </div>

        <h3
          onClick={() => onView(product.slug)}
          style={{
            margin: 0,
            cursor: "pointer",
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        >
          {product.name}
        </h3>

        {hasDiscount ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span
              style={{
                color: T.ink,
                fontWeight: 700,
                fontSize: 17,
              }}
            >
              LKR {parseFloat(product.sale_price).toFixed(2)}
            </span>

            <span
              style={{
                color: T.muted,
                textDecoration: "line-through",
                fontSize: 14,
              }}
            >
              LKR {parseFloat(product.price).toFixed(2)}
            </span>
          </div>
        ) : (
          <div
            style={{
              fontWeight: 700,
              fontSize: 17,
            }}
          >
            LKR {parseFloat(product.price).toFixed(2)}
          </div>
        )}

        {product.funding_goal > 0 && (
          <div style={{ marginTop: 12, textAlign: "left" }}>
            <div
              style={{
                height: 5,
                borderRadius: 3,
                background: T.paper,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(
                    100,
                    (Number(product.funding_raised || 0) / Number(product.funding_goal)) * 100
                  )}%`,
                  background: T.gold,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
                fontSize: 11,
                color: T.muted,
              }}
            >
              <span>LKR {Number(product.funding_raised || 0).toLocaleString()} raised</span>
              {product.roi_percent != null && <span>{product.roi_percent}% ROI</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
